import React, { useState } from 'react';
import { 
  ChartLine, 
  Headset, 
  Storefront,
  SignOut,
  Users,
  ShieldCheck,
  List,
  X
} from '@phosphor-icons/react';

export const AdminDrawerMenu = ({ activeTab, onTabChange, onLogout, ticketCount = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    { id: 'overview', icon: ChartLine, label: 'Overview' },
    { id: 'tickets', icon: Headset, label: 'Tickets', badge: ticketCount },
    { id: 'market', icon: Storefront, label: 'Market Links' },
    { id: 'designers', icon: Users, label: 'All Designers' },
  ];

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
    setIsOpen(false);
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <>
      {/* Header with Hamburger */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIsOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="admin-menu-toggle"
          >
            <List className="h-6 w-6 text-[#121212]" weight="bold" />
          </button>
          <div className="text-center">
            <h1 className="font-['Chivo'] text-xl font-black text-[#FF5A36]">Designbiz.in</h1>
            <p className="text-xs text-green-600 font-medium">Super Admin</p>
          </div>
          <div className="w-10" />
        </div>
      </header>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <div 
        className={`fixed top-0 left-0 h-full w-72 bg-[#1a1a2e] z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-drawer-menu"
      >
        {/* Drawer Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h1 className="font-['Chivo'] text-xl font-black text-[#FF5A36]">Designbiz.in</h1>
            <div className="flex items-center gap-2 mt-1">
              <ShieldCheck className="h-4 w-4 text-green-400" weight="fill" />
              <p className="text-xs text-green-400 font-medium">Super Admin</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="close-admin-drawer"
          >
            <X className="h-5 w-5 text-white" weight="bold" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="py-4 flex-1 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleTabClick(item.id)}
                    className={`flex items-center justify-between w-full px-4 py-3 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-[#FF5A36] text-white' 
                        : 'text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" weight="duotone" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    {item.badge > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        isActive ? 'bg-white text-[#FF5A36]' : 'bg-red-500 text-white'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Quick Stats */}
        <div className="px-4 py-3 border-t border-white/10">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Quick Stats</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-white">7</p>
              <p className="text-xs text-gray-400">Designers</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-[#FF5A36]">{ticketCount}</p>
              <p className="text-xs text-gray-400">Open Tickets</p>
            </div>
          </div>
        </div>

        {/* Logout */}
        <div className="p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-all"
            data-testid="admin-drawer-logout"
          >
            <SignOut className="h-5 w-5" weight="bold" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminDrawerMenu;
